import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useCampaignPerformanceRanking } from '../../hooks/useCampaignPerformanceRanking';
import { CampaignRanking } from '../../types/campaignRanking';
import { TopPerformingCampaignsModal } from '../campaigns/TopPerformingCampaignsModal';

const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
  return num.toString();
};

export const TopPerformingCampaignsCard: React.FC = () => {
  const { data: rankings, isLoading } = useCampaignPerformanceRanking();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const topRankings = rankings?.slice(0, 4) || [];

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row justify-between items-center pb-2">
          <h2 className="text-lg font-bold text-slate-900 font-['Hanken_Grotesk']">
            Top Performing Campaigns
          </h2>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="text-sm font-semibold text-[#A655F8] hover:text-[#8455EF] cursor-pointer transition-colors"
          >
            View All
          </button>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex flex-col gap-2">
                  <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                  <div className="h-2 bg-slate-200 rounded w-full"></div>
                  <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : topRankings.length === 0 ? (
            <div className="text-sm text-slate-500 py-4">
              No campaign data available.
            </div>
          ) : (
            topRankings.map((campaign: CampaignRanking) => {
              const { overallScore, tier } = campaign;
              const { totalViews, totalEngagement } = campaign.metrics;
              const { engagementRate } = campaign.performance;

              let tierClasses = '';
              let barColor = '';
              if (tier === 'High') {
                tierClasses = 'bg-emerald-50 text-emerald-600 border border-emerald-200/60';
                barColor = 'bg-emerald-500';
              } else if (tier === 'Medium') {
                tierClasses = 'bg-amber-50 text-amber-600 border border-amber-200/60';
                barColor = 'bg-amber-500';
              } else {
                tierClasses = 'bg-rose-50 text-rose-600 border border-rose-200/60';
                barColor = 'bg-rose-500';
              }

              return (
                <div key={campaign.id} className="flex flex-col">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-slate-800 text-sm hover:text-[#A655F8] cursor-pointer transition-colors truncate">
                      {campaign.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${tierClasses} font-semibold`}>
                      {overallScore}%
                    </span>
                  </div>
                  
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-1 mb-2">
                    <div 
                      className={`h-full ${barColor} rounded-full`} 
                      style={{ width: `${overallScore}%` }}
                    />
                  </div>

                  <div className="flex gap-4 text-xs text-slate-500">
                    <span>
                      Views <span className="font-semibold text-slate-800">{formatNumber(totalViews)}</span>
                    </span>
                    <span>
                      Engagement <span className="font-semibold text-slate-800">{formatNumber(totalEngagement)}</span>
                    </span>
                    <span>
                      ER <span className="font-semibold text-slate-800">{engagementRate.toFixed(1)}%</span>
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <TopPerformingCampaignsModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)}
          rankings={rankings || []}
        />
      )}
    </>
  );
};
